import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "src/shared/store";
import { ProductSummary } from "../product-modal/model/ProductSummary";
import { ProductModalErrors } from "../product-modal/model/ProductModalError";
import { HighlightedProduct } from "../highlighted-product/model/HighlightedProduct";
import getSummaryFromProduct from "../highlighted-product/util/get-summary-from-product";

const initialErrors = {
  unselectedVariants: [],
  waitForStreamStart: [],
};

const initialProductDetail = (): CombinedModalProduct => ({
  status: "idle",
  errors: initialErrors,
  summary: undefined,
});

export type CombinedModalProduct = {
  status?: "success" | "disabled" | "loading" | "idle";
  summary?: ProductSummary;
  errors: ProductModalErrors;
};
export type CombinedModalProducts = { [id: string]: CombinedModalProduct };

export interface ProductsState {
  products: CombinedModalProducts;
  isVisible: boolean;
}

const initialState: ProductsState = {
  products: {},
  isVisible: false,
};

export const combinedProductModalSlice = createSlice({
  name: "combinedProductModal",
  initialState,
  reducers: {
    setCombinedProductSummaries: (
      state,
      action: PayloadAction<HighlightedProduct[]>
    ) => {
      state.products = {};
      const productDatas = action.payload;
      productDatas.forEach((product) => {
        const newProduct: CombinedModalProduct = {
          status: "idle",
          errors: initialErrors,
          summary: getSummaryFromProduct(product),
        };

        state.products[product?.omniweb_id] = newProduct;
      });
    },
    setIsCombinedModalVisible: (state, action) => {
      state.isVisible = action.payload;
    },
    setCombinedStatus: (state, action) => {
      const { id, status } = action.payload;
      if (!state.products?.[id]) {
        state.products[id] = initialProductDetail();
      }
      state.products[id].status = status;
    },

    addError: (state, action) => {
      const { productId, type, key } = action.payload;
      if (!state.products?.[productId]) {
        state.products[productId] = initialProductDetail();
      }

      if (
        !state.products[productId].errors[type].some(
          (error) => error.key === key
        )
      ) {
        state.products[productId].errors[type].push(action.payload);
      }
    },
    clearErrorType: (state, action) => {
      const { productId, type } = action.payload;
      if (!state.products?.[productId]) return;

      state.products[productId].errors[type] = [];
    },
    clearErrorByProductId: (state, action: PayloadAction<number | string>) => {
      const productId = action.payload;
      if (!productId || !state?.products?.[productId.toString()]) return;
      state.products[productId.toString()].errors = initialErrors;
    },
  },
});

export const {
  setCombinedProductSummaries,
  setIsCombinedModalVisible,
  setCombinedStatus,
  clearErrorType,
  addError,
  clearErrorByProductId,
} = combinedProductModalSlice.actions;

export const isVisibleSelector = (state: RootState) =>
  state.combinedProductModal.isVisible;

export const productsSelector = (state: RootState) =>
  state.combinedProductModal.products;

export default combinedProductModalSlice.reducer;
